import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { X, LayoutGrid, Palette, Wand2, BarChart3, User, LogOut, Clock, ChevronRight } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useMobileSidebar } from '../../contexts/MobileSidebarContext';
import { useCredits } from '../../hooks/useCredits';
import { isFeatureEnabled } from '../../lib/featureFlags';
import { getActiveSubscription } from '../../lib/subscriptions';
import type { SubscriptionWithPlan } from '../../lib/subscriptions';
import { UpgradePlanModal } from '../Profile/UpgradePlanModal';
import { cn } from '../../lib/cn';

type NavItem = {
  icon: typeof LayoutGrid;
  label: string;
  href: string;
  badge?: boolean;
};

export function GlobalMobileSidebar() {
  const { profile, signOut, user } = useAuth();
  const { credits, creditsIncluded, subscriptionUsage } = useCredits();
  const { isOpen, close } = useMobileSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const showSubscriptionWidget = false;
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [subscription, setSubscription] = useState<SubscriptionWithPlan | null>(null);

  useEffect(() => {
    if (user?.id) {
      getActiveSubscription(user.id).then(setSubscription);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!isOpen) {
      document.body.style.overflow = '';
      return;
    }

    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        close();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, close]);

  useEffect(() => {
    close();
  }, [location.pathname, location.search, close]);

  const handleSignOut = async () => {
    close();
    await signOut();
    navigate('/');
  };

  const handleUpgradeClick = () => {
    close();
    setShowUpgradeModal(true);
  };

  const navItems: NavItem[] = [
    { icon: LayoutGrid, label: 'Projects', href: '/dashboard' },
    { icon: Clock, label: 'Results', href: '/dashboard?tab=results' },
    { icon: Palette, label: 'Style Library', href: '/styles' },
    ...(isFeatureEnabled('magicEditor') ? [{ icon: Wand2, label: 'Magic Editor', href: '/editor', badge: true }] : []),
    ...(isFeatureEnabled('analytics') ? [{ icon: BarChart3, label: 'Analytics', href: '/analytics' }] : []),
  ];

  const isActive = (href: string) => {
    if (href === '/dashboard') {
      if (location.pathname.startsWith('/project')) return true;
      if (location.pathname !== '/dashboard') return false;
      return !location.search.includes('tab=results') && !location.search.includes('tab=staging_jobs');
    }
    if (href.startsWith('/dashboard?tab=results')) {
      return location.pathname === '/dashboard'
        && (location.search.includes('tab=results') || location.search.includes('tab=staging_jobs'));
    }
    return location.pathname.startsWith(href);
  };

  const maxCredits = creditsIncluded > 0 ? creditsIncluded : (profile?.credits ?? 100);
  const sidebarSubscription = subscriptionUsage?.subscription ?? null;
  const sidebarPlan = subscriptionUsage?.plan ?? null;
  const isPastDue = sidebarSubscription?.status === 'past_due';
  const isScheduledToCancel = Boolean(
    sidebarSubscription?.cancel_at_period_end && sidebarSubscription?.scheduled_cancellation_date
  );

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const statusMeta = (() => {
    switch (sidebarSubscription?.status) {
      case 'active':
        return { label: 'Active', badge: 'border-olive/20 bg-olive/10 text-olive' };
      case 'trial':
        return { label: 'Trial', badge: 'border-olive/20 bg-olive/10 text-olive' };
      case 'past_due':
        return { label: 'Past Due', badge: 'border-terracotta/20 bg-terracotta/10 text-terracotta' };
      default:
        return { label: 'Free', badge: 'border-stone bg-stone/20 text-sand' };
    }
  })();

  const subscriptionContext = (() => {
    if (!sidebarSubscription) return 'Use credits to stage photos instantly.';
    if (isPastDue) return 'Payment failed. Update billing details to avoid cancellation.';
    if (isScheduledToCancel) {
      return `Cancels on ${formatDate(sidebarSubscription.scheduled_cancellation_date) || 'the end of period'}.`;
    }
    return `Renews ${formatDate(sidebarSubscription.current_period_end) || 'soon'}.`;
  })();

  const handleSubscriptionAction = () => {
    if (!sidebarSubscription) {
      handleUpgradeClick();
      return;
    }

    close();
    navigate('/profile?tab=subscription');
  };

  const initials = profile?.full_name
    ? profile.full_name.split(' ').map((name) => name[0]).join('').toUpperCase().slice(0, 2)
    : 'U';

  if (!user || !profile) return null;

  const drawer = isOpen ? (
    <div className="fixed inset-0 z-40 md:hidden">
      <button
        className="absolute inset-0 bg-charcoal/45"
        onClick={close}
        aria-label="Close navigation drawer"
        type="button"
      />

      <aside
        className="absolute inset-y-0 left-0 flex w-[86vw] max-w-[360px] flex-col border-r border-stone bg-white px-4 pb-[calc(env(safe-area-inset-bottom)+1rem)] pt-[calc(env(safe-area-inset-top)+1rem)] shadow-soft-lg"
        role="dialog"
        aria-modal="true"
        aria-label="Workspace navigation"
      >
        <div className="flex items-center justify-between gap-3 px-2">
          <Link to="/dashboard" onClick={close} className="flex min-w-0 items-center gap-3 rounded-2xl px-2 py-2">
            <img
              src="/AppImages/ios/40.png"
              alt="VirtualStagePro"
              className="size-9 rounded-xl border border-stone object-cover"
            />
            <div className="min-w-0">
              <p className="text-xs text-sand">VirtualStagePro</p>
              <p className="truncate text-sm font-semibold text-charcoal">Workspace</p>
            </div>
          </Link>
          <button
            onClick={close}
            className="flex size-10 items-center justify-center rounded-xl border border-stone bg-alabaster text-charcoal transition-colors duration-150 hover:bg-stone/20"
            aria-label="Close navigation drawer"
            type="button"
          >
            <X className="size-4" />
          </button>
        </div>

        <div className="mt-6 px-2">
          <p className="text-xs uppercase text-sand">Navigation</p>
        </div>

        <nav className="mt-3 flex-1 space-y-1 overflow-y-auto px-1">
          {navItems.map((item) => {
            const active = isActive(item.href);
            return (
              <Link
                key={item.label}
                to={item.href}
                onClick={close}
                className={cn(
                  'group flex items-center gap-3 rounded-xl border px-3 py-3 text-sm transition-colors duration-150',
                  active
                    ? 'border-olive/20 bg-olive/10 text-charcoal'
                    : 'border-transparent text-sand hover:border-stone hover:bg-stone/20 hover:text-charcoal'
                )}
              >
                <div
                  className={cn(
                    'flex size-9 items-center justify-center rounded-lg border transition-colors duration-150',
                    active
                      ? 'border-olive/20 bg-white text-olive'
                      : 'border-stone bg-white text-sand group-hover:text-charcoal'
                  )}
                >
                  <item.icon className="size-4" />
                </div>
                <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
                  <span className="truncate font-medium">{item.label}</span>
                  {item.badge ? (
                    <span className="size-2 rounded-full bg-terracotta" />
                  ) : active ? (
                    <ChevronRight className="size-4 text-olive" />
                  ) : null}
                </div>
              </Link>
            );
          })}
        </nav>

        <div className="space-y-3 border-t border-stone pt-4">
          {showSubscriptionWidget && (
            <div className="rounded-2xl border border-stone bg-alabaster p-4">
              <div className="flex items-center justify-between gap-3">
                <p className="truncate text-sm font-semibold text-charcoal">{sidebarPlan?.name || 'Free Plan'}</p>
                <span className={cn('rounded-full border px-2 py-1 text-[11px]', statusMeta.badge)}>
                  {statusMeta.label}
                </span>
              </div>
              <p className="mt-2 text-xs text-sand text-pretty">
                {credits}/{maxCredits} credits
              </p>
              <p className="mt-2 text-xs text-sand text-pretty">{subscriptionContext}</p>
              <button
                onClick={handleSubscriptionAction}
                className="mt-4 inline-flex w-full items-center justify-center rounded-xl border border-stone bg-white px-3 py-2 text-sm font-medium text-charcoal transition-colors duration-150 hover:bg-stone/20"
                type="button"
              >
                {isPastDue ? 'Update Payment' : sidebarSubscription ? 'Manage Subscription' : 'Upgrade'}
              </button>
            </div>
          )}

          <Link
            to="/profile"
            onClick={close}
            className="flex items-center gap-3 rounded-2xl border border-stone bg-alabaster px-3 py-3 transition-colors duration-150 hover:bg-stone/20"
          >
            <div className="flex size-11 items-center justify-center rounded-xl bg-charcoal text-sm font-semibold text-white">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-charcoal">{profile.full_name || 'User'}</p>
              <p className="truncate text-xs text-sand">{profile.email}</p>
            </div>
            <User className="size-4 text-sand" />
          </Link>

          <button
            onClick={handleSignOut}
            className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-terracotta transition-colors duration-150 hover:bg-terracotta/5"
            type="button"
          >
            <LogOut className="size-4" />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>
    </div>
  ) : null;

  return createPortal(
    <>
      {drawer}
      {showUpgradeModal && user && (
        <UpgradePlanModal
          currentSubscription={subscription}
          userId={user.id}
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          onSuccess={() => {
            if (user.id) {
              getActiveSubscription(user.id).then(setSubscription);
            }
          }}
        />
      )}
    </>,
    document.body
  );
}
